import { createContext, useContext, useState, useEffect, useCallback } from "react";

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map FastAPI error detail (string | array | object) to a safe string.
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  // null = still checking, false = not authenticated, object = authenticated
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, { credentials: "include" });
      if (!res.ok) throw new Error("Not authenticated");
      const data = await res.json();
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: if returning from Google OAuth callback, skip the /me check.
    // AuthCallback exchanges the session_id and establishes the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
    setUser(data);
    return data;
  }, []);

  const signup = useCallback(async (email, password, name) => {
    const res = await fetch(`${API_URL}/api/auth/signup`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, password, name }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
    setUser(data);
    return data;
  }, []);

  const sendVerification = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/auth/send-verification`, {
      method: "POST",
      credentials: "include",
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
    return data;
  }, []);

  const verifyEmail = useCallback(async (code) => {
    const res = await fetch(`${API_URL}/api/auth/verify-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ code }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
    setUser((prev) => (prev ? { ...prev, verified: true } : prev));
    return data;
  }, []);

  const setPassword = useCallback(async (password) => {
    const res = await fetch(`${API_URL}/api/auth/set-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
    setUser((prev) => (prev ? { ...prev, has_password: true } : prev));
    return data;
  }, []);

  const verifyAdmin = useCallback(async (password) => {
    const res = await fetch(`${API_URL}/api/admin/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
    return data;
  }, []);

  const adminLogout = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/admin/logout`, { method: "POST", credentials: "include" });
    } catch (err) {
      console.error("Admin logout failed:", err);
    }
  }, []);

  const loginWithGoogle = useCallback(() => {    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, { method: "POST", credentials: "include" });
    } catch (err) {
      // Non-fatal: clear local session regardless of network outcome.
      console.error("Logout request failed:", err);
    }
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, loading, isAuthenticated: !!user, login, signup, loginWithGoogle, logout, checkAuth, setUser, sendVerification, verifyEmail, setPassword, verifyAdmin, adminLogout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
